import { SettingsOutlined as SettingsIcon } from '@mui/icons-material';
import { Alert, Chip, Grid } from '@mui/material';
import { toNumber } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormProvider } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AssemblyLayout } from 'src/components/assemblyLayout';
import { AssemblyScreen } from 'src/components/assemblyScreen';
import { AssemblyScreenSection } from 'src/components/assemblyScreenSection';
import { LayoutBuilderWithDisclaimer } from 'src/components/layoutBuilder';
import { calculateIsGridFull } from 'src/components/layoutBuilder/LayoutBuilder.helper';
import { ScreenConfigModal } from 'src/components/modal/screenConfigModal/ScreenConfigModal';
import { Snackbar } from 'src/components/snackbar';
import { PATHS } from 'src/constants/paths';
import { useCreateRotationScreenSection, useModal } from 'src/hooks';
import { useDeleteRotationScreenSection } from 'src/hooks/api/layouts/useDeleteRotationScreenSection';
import { useNavigateBlocker } from 'src/hooks/navigation';
import { type ILayoutConfig, ISidebarStates } from 'src/interfaces';
import {
  isCurrentScreenWithCOD,
  isCurrentScreenWithCODThankYou,
  isCurrentScreenWithDYR,
  lang,
  parseLayoutConfig,
  useUserRole,
} from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import {
  CreateSectionsLayoutsContainer,
  CreateSectionsLayoutsContentContainer,
} from '../createSectionsLayoutsPage/CreateSectionsLayoutsPage.styled';
import { useCreateLayoutsPage } from '../createSectionsLayoutsPage/hooks/useCreateLayoutsPage.hook';
import { ChipSettingsContainer } from '../editLayoutsPage/EditLayoutsPage.styled';
import { useEditLayoutsPage } from '../editLayoutsPage/hooks/useEditLayoutsPage.hook';
import { LayoutsEditPageHeader } from '../editLayoutsPage/modules/LayoutsEditPageHeader/LayoutsEditPageHeader';
import {
  createRotationSectionPayload,
  validateRotationSections,
} from './CreateRotationSectionsLayoutsPage.helper';
import {
  CreateRotationSectionsGridContainer,
  RotationSectionsDefaultLayout,
  RotationSectionsLayoutBuilder,
} from './CreateRotationSectionsLayoutsPage.style';
import { useCreateRotationLayoutsPage } from './hooks/useCreateRotationLayoutsPage.hook';

export const CreateRotationSectionsLayoutsPage = () => {
  const { isViewOnly } = useUserRole();
  const { layoutId = '' } = useParams();
  const { layouts, screenNames, formMethods } = useEditLayoutsPage(
    toNumber(layoutId),
  );
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const screenIndex = queryParams.get('screen') ?? '0';
  const screenName = screenNames?.[toNumber(screenIndex) - 1];
  const navigate = useNavigate();

  const [showLeaveModal, setShowLeaveModal] = useState(true);

  useNavigateBlocker(showLeaveModal);

  const {
    rotationGridSections,
    setRotationGridSections,
    gridSectionHeight,
    gridSectionWidth,
    globalComponentsScreen,
  } = useCreateRotationLayoutsPage(toNumber(layoutId), toNumber(screenIndex));

  const {
    defaultSections,
    disclaimerWidth,
    disclaimerHeightScaled,
    extraHeightForDisclaimerTopAdjustment,
  } = useCreateLayoutsPage(toNumber(layoutId), toNumber(screenIndex), true);

  const [defaultLayout] = layouts;
  const defaultLayoutZoomedScreen =
    defaultLayout?.screens[toNumber(screenIndex) - 1];
  const hasRotationGridChanged = useMemo(
    () =>
      defaultLayout?.config?.gridLines?.[toNumber(screenIndex) - 1] !==
      defaultLayout?.config?.rotationGridLines?.[toNumber(screenIndex) - 1],
    [defaultLayout, screenIndex],
  );

  const screenConfigModal = useModal();
  const createRotationScreenSection = useCreateRotationScreenSection();
  const deleteRotationScreenSection = useDeleteRotationScreenSection();

  const {
    orientation,
    cols,
    rows,
    disclaimerPosition,
    disclaimerType,
  }: ILayoutConfig = parseLayoutConfig(
    defaultLayout?.config?.rotationGridLines?.[toNumber(screenIndex) - 1] ??
      'vertical3x3Grid',
  );

  const navigateBack = useCallback(
    () =>
      navigate(PATHS.ASSEMBLY_LAYOUTS_EDIT(layoutId), {
        state: { sidebar: ISidebarStates.ROTATION },
      }),
    [layoutId, navigate],
  );

  const isRotationSectionsValid = useMemo(() => {
    if (rotationGridSections.length === 0) {
      return true;
    }

    if (hasRotationGridChanged) {
      return calculateIsGridFull(
        rotationGridSections,
        toNumber(rows),
        toNumber(cols),
      );
    }

    return validateRotationSections(
      rotationGridSections,
      defaultSections ?? [],
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotationGridSections, hasRotationGridChanged]);

  const { hasCOD, hasThankYou, hasDYR } = checkSectionTypes(
    defaultLayout?.config?.sectionTypes,
  );

  const handleOpenConfig = () => {
    formMethods.setValue(`orientation`, orientation);
    formMethods.setValue(`screen_grid`, `${cols} X ${rows}`);
    formMethods.setValue('disclaimer', !!disclaimerPosition);
    formMethods.setValue('size', disclaimerType);
    formMethods.setValue('placement', disclaimerPosition);
    formMethods.clearErrors();
    screenConfigModal.onOpenModal();
  };

  const handleSaveScreen = async () => {
    const sectionPayload = createRotationSectionPayload(
      rotationGridSections,
      { height: gridSectionHeight, width: gridSectionWidth },
      extraHeightForDisclaimerTopAdjustment,
    );

    if (
      defaultLayout?.screens?.[Number(screenIndex) - 1].sections.some(
        (section) => section.config?.events?.SECTION_TIMER !== undefined,
      )
    ) {
      await deleteRotationScreenSection.mutateAsync({
        screen: defaultLayout?.screens?.[Number(screenIndex) - 1],
      });
    }

    if (sectionPayload.length > 0) {
      await createRotationScreenSection.mutateAsync({
        sections: [...sectionPayload],
        screen: defaultLayout?.screens?.[Number(screenIndex) - 1],
        layoutId,
        layoutGridLine:
          defaultLayout?.config?.rotationGridLines?.[Number(screenIndex) - 1] ??
          '',
      });
    }

    flushSync(() => {
      setShowLeaveModal(false);
    });

    Snackbar.success(lang.editLayoutsSidebar.sectionCreateSuccess(screenName));

    navigateBack();
  };

  const hasCODSection =
    hasCOD &&
    isCurrentScreenWithCOD(toNumber(screenIndex), globalComponentsScreen);
  const hasThankYouOrDYRSection =
    (isCurrentScreenWithCODThankYou(
      toNumber(screenIndex),
      globalComponentsScreen,
    ) ||
      isCurrentScreenWithDYR(toNumber(screenIndex), globalComponentsScreen)) &&
    (hasThankYou || hasDYR);
  const hasNotFilledDefaultLayout = defaultLayout?.screens?.some(
    (screen) =>
      screen?.name === 'placeholderHorizontal' ||
      screen?.name === 'placeholderVertical',
  );

  const isRotationDisabled =
    hasCODSection || hasThankYouOrDYRSection || hasNotFilledDefaultLayout;

  return (
    <FormProvider {...formMethods}>
      <CreateSectionsLayoutsContainer>
        <CreateSectionsLayoutsContentContainer>
          <LayoutsEditPageHeader
            layoutName={defaultLayout?.name}
            screenName={screenName}
            navigateBack={navigateBack}
            isViewOnly={isViewOnly}
            isLoading={
              createRotationScreenSection.isLoading ||
              deleteRotationScreenSection.isLoading
            }
            handleSaveScreen={handleSaveScreen}
            isSaveDisabled={!isRotationSectionsValid || isRotationDisabled}
          />
          <CreateRotationSectionsGridContainer>
            <RotationSectionsLayoutBuilder data-testid="rotation-section-layout-builder">
              <Alert
                severity="info"
                icon={false}
                sx={{
                  marginTop: '20px',
                  marginBottom: hasRotationGridChanged ? '10px' : '20px',
                  width: '320px',
                  display: 'block',
                  textAlign: 'center',
                }}>
                {lang.editLayoutsSidebar.createSectionAlert}
              </Alert>
              {hasRotationGridChanged && (
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    marginTop: '10px',
                    marginBottom: '20px',
                    width: '320px',
                    display: 'block',
                    textAlign: 'center',
                  }}>
                  {lang.editLayoutsSidebar.rotationGridChangeAlert}
                </Alert>
              )}
              <ChipSettingsContainer>
                <Chip label={`Screen ${screenIndex}`} color="primary" />
                {!isRotationDisabled && (
                  <SettingsIcon
                    color="secondary"
                    style={{ cursor: 'pointer' }}
                    onClick={handleOpenConfig}
                    data-testid="screen-setting-icon"
                  />
                )}
              </ChipSettingsContainer>
              <LayoutBuilderWithDisclaimer
                gridSections={rotationGridSections}
                rows={rows}
                cols={cols}
                cellHeight={gridSectionHeight}
                cellWidth={gridSectionWidth}
                setGridSections={setRotationGridSections}
                isRecommendationEnabled={false}
                disclaimerPosition={disclaimerPosition}
                disclaimerType={disclaimerType}
                disclaimerHeightScaled={disclaimerHeightScaled}
                disclaimerWidth={disclaimerWidth}
              />
            </RotationSectionsLayoutBuilder>
            <RotationSectionsDefaultLayout>
              <Alert
                severity="info"
                icon={false}
                sx={{ maxWidth: '300px', textAlign: 'center' }}>
                {lang.editLayoutsSidebar.defaultLayoutDragSectionAlert}
              </Alert>
              <Chip label={lang.editLayoutsSidebar.defaultLayout} />
              <Grid item key={defaultLayout?.id}>
                <AssemblyLayout>
                  <AssemblyScreen
                    key={defaultLayoutZoomedScreen?.name}
                    height={defaultLayoutZoomedScreen?.height}
                    width={defaultLayoutZoomedScreen?.width}
                    type="editor"
                    scale={0.2}
                    selectable={false}>
                    {defaultLayoutZoomedScreen?.sections.map(
                      (section, index) => (
                        <AssemblyScreenSection
                          key={section.name + index}
                          section={section}
                          sectionTypes={defaultLayout.config?.sectionTypes}
                          scale={0.2}
                          selectable={false}
                          showEditIcon={false}
                          isLayoutList
                        />
                      ),
                    )}
                  </AssemblyScreen>
                </AssemblyLayout>
              </Grid>
            </RotationSectionsDefaultLayout>
          </CreateRotationSectionsGridContainer>
        </CreateSectionsLayoutsContentContainer>
      </CreateSectionsLayoutsContainer>
      <ScreenConfigModal
        screenConfigModal={screenConfigModal}
        layout={defaultLayout}
        formMethods={formMethods}
        configScreenIndex={toNumber(screenIndex) - 1}
        layoutId={layoutId ?? ''}
        isRotationScreen
        setRotationGridSections={setRotationGridSections}
        screenName={screenName}
      />
    </FormProvider>
  );
};
